import { Component, inject, isDevMode, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { Empty } from './components/atoms/empty/empty';
import { importDialogOpen } from './components/organisms/dialogs/import-products-dialog/import-products-dialog.state';
import {
  appRouteCommands,
  isActivitiesProductUpdateUrl,
  isActivitiesSyncUrl,
} from './app-route-paths';
import { AuthService } from './services/auth-service';
import { CompanyRealtimeService } from './services/company-realtime.service';
import { ThemeService } from './services/theme.service';
import {
  getLanguageFromPath,
  navigateToLanguagePath,
  normalizeAppLocale,
  setAppLanguage,
} from './utils/internationalization';
/** Whether the URL is a public (no-auth) page, with or without a locale prefix. */
function isPublicRoute(): boolean {
  return window.location.pathname.split('/').includes('public');
}

/**
 * Flags keyboard navigation on <html> for the focus ring of text fields (see
 * primeNg.css): Tab turns it on, any pointer press turns it off.
 */
function trackKeyboardNavigation(): void {
  const root = document.documentElement;
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Tab') root.classList.add('keyboard-nav');
    },
    true,
  );
  document.addEventListener(
    'pointerdown',
    () => root.classList.remove('keyboard-nav'),
    true,
  );
}

@Component({
  selector: 'app-root',
  imports: [Button, ConfirmDialogModule, Empty, RouterOutlet, ToastModule],
  providers: [],
  template: `<router-outlet />

    @if (bootFailed()) {
      <app-empty
        class="flex h-dvh items-center justify-center p-6"
        text="Non è stato possibile avviare l'app: controlla la connessione."
        i18n-text
      >
        <div class="mt-4 flex justify-center">
          <p-button
            (onClick)="reload()"
            label="Riprova"
            severity="secondary"
            size="small"
            i18n-label
          />
        </div>
      </app-empty>
    }

    <p-confirmdialog #cd appendTo="body" i18n-header>
      <ng-template
        #headless
        let-message
        let-onAccept="onAccept"
        let-onReject="onReject"
      >
        <div class="w-md flex flex-col py-4 lg:py-4 overflow-hidden">
          <header
            class="rounded-t-lg px-6 pb-4 flex items-center justify-between"
          >
            <h4 class="font-medium! text-xl!">{{ message.header }}</h4>
          </header>

          <!-- content -->
          <div
            class="flex px-6 bg-surface-background py-4 border-y flex-col gap-3 flex-1 overflow-y-auto"
          >
            <p class="text-surface-text whitespace-pre-line">
              {{ message.message }}
            </p>
          </div>

          <!-- footer -->
          <div class="px-6 rounded-b-lg flex justify-end gap-2 mt-4">
            <footer class="flex justify-end gap-2 w-full">
              <p-button
                (onClick)="onReject()"
                label="Annulla"
                i18n-label
                severity="secondary"
                size="small"
              />
              <p-button
                [severity]="message?.acceptButtonProps?.severity ?? 'danger'"
                [label]="message.acceptLabel ?? confirmLabel"
                (onClick)="onAccept()"
                size="small"
              />
            </footer>
          </div>
        </div>
      </ng-template>
    </p-confirmdialog>

    @if (!isImportDialogOpen()) {
      <p-toast [position]="'bottom-left'">
        <ng-template #message let-message>
          <div class="flex flex-col gap-1 flex-1">
            <div class="flex flex-row gap-1 items-center">
              <i
                [class]="severityIcon(message.severity)"
                aria-hidden="true"
              ></i>
              <span class="font-semibold">{{ message.summary }}</span>
            </div>
            <span>{{ message.detail }}</span>
            @if (message.data?.importId && !isImportDialogOpen()) {
              <p-button
                class="mt-1 self-start"
                [label]="displayLabel"
                (onClick)="openImportDrawer(message.data.importId)"
                size="small"
                severity="secondary"
              />
            }
            @if (
              message.data?.productUpdateId &&
              !isOnProductUpdateDrawer(message.data.productUpdateId)
            ) {
              <p-button
                class="mt-1 self-start"
                [label]="displayLabel"
                (onClick)="
                  openProductUpdateDrawer(message.data.productUpdateId)
                "
                size="small"
                severity="secondary"
              />
            }
            @if (message.data?.syncId && !isOnSyncDrawer(message.data.syncId)) {
              <p-button
                class="mt-1 self-start"
                [label]="displayLabel"
                (onClick)="openSyncDrawer(message.data.syncId)"
                size="small"
                severity="secondary"
              />
            }
          </div>
        </ng-template>
      </p-toast>
    } `,
})
export class App {
  readonly confirmLabel = $localize`Sì, confermo`;

  readonly authService = inject(AuthService);
  // Injected as a field, not in the constructor body: the appearance has to
  // follow the user's preference on public pages too, and those bail out of
  // the auth bootstrap below.
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly messageService = inject(MessageService);

  readonly displayLabel = $localize`Visualizza`;

  readonly isImportDialogOpen = importDialogOpen;

  /** Without `me` the guards wait for ever: the page would stay blank. */
  protected readonly bootFailed = signal(false);

  protected reload(): void {
    window.location.reload();
  }

  severityIcon(severity: string): string {
    const map: Record<string, string> = {
      success: 'pi pi-check-circle',
      error: 'pi pi-times-circle',
      warn: 'pi pi-exclamation-triangle',
      info: 'pi pi-info-circle',
    };
    return map[severity] ?? 'pi pi-info-circle';
  }

  /** An import is a route, like a sync run: the toast navigates to its drawer. */
  openImportDrawer(importId: string): void {
    if (this.isImportDialogOpen()) return;
    void this.router.navigate(appRouteCommands.activitiesImport(importId));
    setTimeout(() => this.messageService.clear());
  }

  /** A run is a route, so the toast navigates to it instead of opening a panel. */
  openSyncDrawer(syncId: string): void {
    void this.router.navigate(appRouteCommands.activitiesSync(syncId));
    setTimeout(() => this.messageService.clear());
  }

  isOnSyncDrawer(syncId: string): boolean {
    return isActivitiesSyncUrl(this.router.url, syncId);
  }

  openProductUpdateDrawer(activityId: string): void {
    void this.router.navigate(
      appRouteCommands.activitiesProductUpdate(activityId),
    );
    setTimeout(() => this.messageService.clear());
  }

  isOnProductUpdateDrawer(activityId: string): boolean {
    return isActivitiesProductUpdateUrl(this.router.url, activityId);
  }

  constructor() {
    trackKeyboardNavigation();

    // Public pages (e.g. shared collections) must render without auth — skip
    // the auth bootstrap so it doesn't redirect anonymous visitors to login.
    if (isPublicRoute()) return;

    inject(CompanyRealtimeService);
    this.authService
      .init()
      .then((fromServer) => {
        // Another language's bundles may not be on the device
        if (!fromServer) return;
        const localeInUrl = getLanguageFromPath();

        if (!isDevMode()) {
          const locale = normalizeAppLocale(
            this.authService.me()?.preferredCulture,
          );

          if (!locale || localeInUrl === locale) return;

          setAppLanguage(locale);
          navigateToLanguagePath(locale);
        }
      })
      .catch(() => this.bootFailed.set(true));
  }
}
